import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Tooltip,
  Typography,
  keyframes,
  styled,
} from "@mui/material";
import { HomeCampusIcon } from "./IconHomeCampus";
import { MagnetIcon } from "./IconMagnet";
import { SectionHeader } from "./styles";
import type { DistrictProgramForHat, DistrictSchoolForHat } from "../../types";

const PROGRAM_CARD_HEIGHT = 70;

const CALCULATING_DURATION_MS = 2200;

const dotPulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.5; }
  50% { transform: scale(1.4); opacity: 1; }
`;

const SeeYourMatchesButton = styled(Button, {
  label: "DistrictSeeYourMatches",
})(() => ({
  color: "#ffffff",
  borderColor: "rgba(255, 255, 255, 0.32)",
  fontFamily: "Proxima Nova, sans-serif",
  fontWeight: 600,
  fontSize: "14px",
  textTransform: "none",
  borderRadius: "8px",
  paddingLeft: 18,
  paddingRight: 18,
  alignSelf: "center",
  "&:hover": {
    borderColor: "rgba(255, 255, 255, 0.48)",
    backgroundColor: "rgba(255, 255, 255, 0.08)",
  },
}));

const CalculatingDot = styled(Box, {
  label: "DistrictCalculatingDot",
  shouldForwardProp: (prop) => prop !== "dotColor" && prop !== "delay",
})<{ dotColor: string; delay: number }>(({ dotColor, delay }) => ({
  width: 12,
  height: 12,
  borderRadius: "50%",
  backgroundColor: dotColor,
  animation: `${dotPulse} 0.7s ${delay}s ease-in-out infinite`,
}));

const CalculatingLabel = styled(Typography, {
  label: "DistrictCalculatingLabel",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontStyle: "italic",
  fontWeight: 400,
  fontSize: "14px",
  lineHeight: "22px",
  color: "#fff5cc",
  textAlign: "center",
}));

const CALCULATING_DOTS = [
  { color: "#c684ff", delay: 0 },
  { color: "#fa830d", delay: 0.175 },
  { color: "#00b8d9", delay: 0.35 },
  { color: "#36b37e", delay: 0.525 },
];

const WandIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <path
      d="M3 15L12.5 5.5L13.5 6.5L4 16L3 15Z"
      stroke="#fff"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
    <path
      d="M14 3L14.5 4.5L16 5L14.5 5.5L14 7L13.5 5.5L12 5L13.5 4.5L14 3Z"
      fill="#fff"
    />
    <path
      d="M7 2L7.4 3.1L8.5 3.5L7.4 3.9L7 5L6.6 3.9L5.5 3.5L6.6 3.1L7 2Z"
      fill="#fff"
    />
    <path
      d="M16 10L16.4 11.1L17.5 11.5L16.4 11.9L16 13L15.6 11.9L14.5 11.5L15.6 11.1L16 10Z"
      fill="#fff"
    />
  </svg>
);

const CalculatingMatches = ({ onComplete }: { onComplete: () => void }) => {
  useEffect(() => {
    const timer = setTimeout(onComplete, CALCULATING_DURATION_MS);
    return () => clearTimeout(timer);
  }, [onComplete]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 1.25,
        mt: 3,
      }}
    >
      <Box sx={{ display: "flex", gap: 1.25, alignItems: "center" }}>
        {CALCULATING_DOTS.map(({ color, delay }) => (
          <CalculatingDot key={color} dotColor={color} delay={delay} />
        ))}
      </Box>
      <CalculatingLabel>Calculating your matches…</CalculatingLabel>
    </Box>
  );
};

const ProgramCard = styled(Box, {
  label: "DistrictProgramCard",
  shouldForwardProp: (prop) => prop !== "selected",
})<{ selected?: boolean }>(({ selected }) => ({
  minWidth: 0,
  height: PROGRAM_CARD_HEIGHT,
  display: "flex",
  flexDirection: "column",
  gap: 4,
  padding: "6px 8px",
  borderRadius: 8,
  cursor: "pointer",
  backgroundColor: selected ? "#ffffff" : "rgba(255,255,255,0.75)",
  border: selected ? "2px solid rgba(142,51,255,0.6)" : "2px solid transparent",
  transition: "border-color 0.15s, background-color 0.15s",
  "&:hover": {
    backgroundColor: "#ffffff",
  },
}));

const BadgeBox = styled(Box, {
  label: "DistrictMatchBadge",
  shouldForwardProp: (prop) => prop !== "matchType",
})<{ matchType: "BEST_MATCH" | "CLOSE_MATCH" }>(({ matchType }) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "0 6px",
  borderRadius: 6,
  height: 18,
  backgroundColor: matchType === "BEST_MATCH" ? "#8e33ff" : "#ffab00",
  fontFamily: "Proxima Nova, sans-serif",
  fontWeight: 700,
  fontSize: "10px",
  lineHeight: "20px",
  color: matchType === "BEST_MATCH" ? "#ffffff" : "#1c252e",
  textTransform: "uppercase",
  whiteSpace: "nowrap",
  alignSelf: "flex-start",
}));

const ProgramCardName = styled(Typography, {
  label: "DistrictProgramCardName",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontWeight: 500,
  fontSize: "12px",
  lineHeight: "18px",
  color: "rgba(0,0,0,0.8)",
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
}));

const SchoolDetailCard = styled(Box, {
  label: "DistrictSchoolDetailCard",
})(() => ({
  display: "flex",
  flexDirection: "column",
  gap: 4,
  padding: "8px 16px",
  borderRadius: 16,
  border: "1px solid rgba(255,255,255,1)",
  backdropFilter: "blur(30px)",
  backgroundColor: "rgba(255,255,255,0.08)",
  flexShrink: 0,
}));

const SchoolChip = styled(Box, { label: "DistrictSchoolChip" })(() => ({
  display: "flex",
  alignItems: "center",
  gap: 0,
  height: 24,
  paddingLeft: 3,
  paddingRight: 5,
  borderRadius: 8,
  backgroundColor: "rgba(255,255,255,0.05)",
  flex: "1 1 calc(50% - 4px)",
  minWidth: 0,
  overflow: "hidden",
}));

const SchoolChipLabel = styled(Typography, {
  label: "DistrictSchoolChipLabel",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontWeight: 500,
  fontSize: "13px",
  lineHeight: "18px",
  color: "#ffffff",
  padding: "0 5px",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  minWidth: 0,
  flex: 1,
}));

// --- Helper components ---

const SchoolTypeIcon = ({
  matchType,
}: {
  matchType: DistrictSchoolForHat["matchType"];
}) => (
  <Box
    sx={{
      width: 18,
      height: 18,
      flexShrink: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    {matchType === "HOME_CAMPUS" ? <HomeCampusIcon /> : <MagnetIcon />}
  </Box>
);

// --- Main component ---

export const DistrictProgramsSection = ({
  programs,
  revealed = false,
  onRevealed,
}: {
  programs: DistrictProgramForHat[];
  /** When true, skip the "See Your Matches" button + animation and render results immediately. */
  revealed?: boolean;
  /** Fires once the calculating animation completes. */
  onRevealed?: () => void;
}) => {
  // Sort: best matches first, close matches next
  const sorted = [...programs].sort((a, b) => {
    if (a.matchType === b.matchType) return 0;
    return a.matchType === "BEST_MATCH" ? -1 : 1;
  });

  const [selectedId, setSelectedId] = useState<string | null>(
    revealed ? (sorted[0]?.id ?? null) : null,
  );
  const [showMatches, setShowMatches] = useState(revealed);
  const [calculating, setCalculating] = useState(false);

  // Sync with the cached reveal flag if it loads after the initial render.
  useEffect(() => {
    if (revealed && !showMatches) {
      setShowMatches(true);
      setSelectedId((prev) => prev ?? sorted[0]?.id ?? null);
    }
  }, [revealed, showMatches, sorted]);

  const toggle = (id: string) =>
    setSelectedId((prev) => (prev === id ? null : id));

  const selectedProgram = programs.find((p) => p.id === selectedId) ?? null;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        flex: "1 0 0",
      }}
    >
      <SectionHeader>High School Matches</SectionHeader>

      {!showMatches && !calculating && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
          <SeeYourMatchesButton
            variant="outlined"
            onClick={() => setCalculating(true)}
            startIcon={<WandIcon />}
          >
            See Your Matches
          </SeeYourMatchesButton>
        </Box>
      )}

      {calculating && (
        <CalculatingMatches
          onComplete={() => {
            setCalculating(false);
            setShowMatches(true);
            setSelectedId(sorted[0]?.id ?? null);
            onRevealed?.();
          }}
        />
      )}

      {showMatches && (
        <>
          <Typography
            variant="subtitle2"
            sx={{
              color: "#fff",
              mb: 1,
              mt: 1,
            }}
          >
            PROGRAMS IN YOUR AREA
          </Typography>
          <Box
            sx={{
              overflow: "scroll",
              minHeight: "226px",
              maxHeight: "226px",
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 1,
              }}
            >
              {sorted.map((program) => (
                <ProgramCard
                  key={program.id}
                  selected={selectedId === program.id}
                  onClick={() => toggle(program.id)}
                >
                  <BadgeBox matchType={program.matchType}>
                    {program.matchType === "BEST_MATCH"
                      ? "Best Match"
                      : "Close Match"}
                  </BadgeBox>
                  <Tooltip
                    placement="top"
                    title={
                      <Box>
                        <Typography
                          sx={{
                            fontSize: "12px",
                            fontWeight: 600,
                            lineHeight: "18px",
                          }}
                        >
                          {program.name}
                        </Typography>
                        {program.matchInfo && (
                          <Typography
                            sx={{
                              fontSize: "11px",
                              lineHeight: "16px",
                              opacity: 0.8,
                              mt: 0.25,
                            }}
                          >
                            {program.matchInfo}
                          </Typography>
                        )}
                      </Box>
                    }
                  >
                    <ProgramCardName>{program.name}</ProgramCardName>
                  </Tooltip>
                </ProgramCard>
              ))}
            </Box>
          </Box>

          {selectedProgram && (
            <SchoolDetailCard sx={{ mt: 0.5, height: 106, overflow: "hidden" }}>
              <Tooltip title={selectedProgram.name} placement="top">
                <Typography
                  sx={{
                    fontFamily: "Proxima Nova, sans-serif",
                    fontWeight: 600,
                    fontSize: "18px",
                    lineHeight: "28px",
                    color: "#ffffff",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {selectedProgram.name}
                </Typography>
              </Tooltip>
              {selectedProgram.schools.length > 0 && (
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    display: "flex",
                    flexWrap: "wrap",
                    alignContent: "flex-start",
                    gap: 1,
                    mt: 0.5,
                    overflowY: "auto",
                  }}
                >
                  {selectedProgram.schools.map((school) => (
                    <Tooltip
                      key={school.id}
                      title={school.name}
                      placement="top"
                    >
                      <SchoolChip>
                        <SchoolTypeIcon matchType={school.matchType} />
                        <SchoolChipLabel>{school.name}</SchoolChipLabel>
                      </SchoolChip>
                    </Tooltip>
                  ))}
                </Box>
              )}
            </SchoolDetailCard>
          )}
        </>
      )}
    </Box>
  );
};
